import { useCallback, useRef } from "react";

/**
 * Subtle hover parallax — the attached element drifts a few pixels against
 * cursor movement inside its container. Disabled for reduced-motion users
 * and effectively inert on touch devices.
 * Usage: const { ref, onMove, onLeave } = useHoverParallax();
 *        <div onMouseMove={onMove} onMouseLeave={onLeave}>
 *          <div ref={ref} className="parallax-frame">…</div>
 *        </div>
 */
export function useHoverParallax(strength = 8) {
  const ref = useRef(null);

  const onMove = useCallback(
    (e) => {
      const el = ref.current;
      if (!el) return;
      if (
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      )
        return;
      const r = e.currentTarget.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.transform = `translate3d(${(-x * strength).toFixed(1)}px, ${(-y * strength).toFixed(1)}px, 0)`;
    },
    [strength]
  );

  const onLeave = useCallback(() => {
    if (ref.current) ref.current.style.transform = "translate3d(0, 0, 0)";
  }, []);

  return { ref, onMove, onLeave };
}
